import { forwardRef, useEffect, useImperativeHandle, useRef } from "react";
import { loadTurnstile } from "plg/auth/turnstileLoader";
import type { TurnstileApi } from "plg/auth/turnstileLoader";

export interface TurnstileHandle {
  /** A token is single use: call after every send so the next press gets a fresh one. */
  reset: () => void;
}

interface Props {
  /** The invisible or the managed (checkbox) widget's site key; a new key renders a new widget. */
  siteKey: string;
  /** true for the managed widget, so the checkbox always shows. */
  visible: boolean;
  /** The current token, or null when there is none (mount, expiry, error, reset). */
  onToken: (token: string | null) => void;
  onLoadError?: () => void;
}

const TurnstileWidget = forwardRef<TurnstileHandle, Props>(function TurnstileWidget(
  { siteKey, visible, onToken, onLoadError },
  ref
) {
  const container = useRef<HTMLDivElement>(null);
  const api = useRef<TurnstileApi | null>(null);
  const widgetId = useRef<string | null>(null);
  const onTokenRef = useRef(onToken);
  const onLoadErrorRef = useRef(onLoadError);
  onTokenRef.current = onToken;
  onLoadErrorRef.current = onLoadError;

  useEffect(() => {
    let cancelled = false;
    onTokenRef.current(null);
    loadTurnstile()
      .then((turnstile) => {
        if (cancelled || !container.current) return;
        api.current = turnstile;
        widgetId.current = turnstile.render(container.current, {
          sitekey: siteKey,
          ...(visible ? { appearance: "always" as const } : {}),
          callback: (token) => onTokenRef.current(token),
          "expired-callback": () => {
            onTokenRef.current(null);
            if (widgetId.current) turnstile.reset(widgetId.current);
          },
          "error-callback": () => onTokenRef.current(null),
        });
      })
      .catch(() => {
        if (!cancelled) onLoadErrorRef.current?.();
      });
    return () => {
      cancelled = true;
      if (api.current && widgetId.current) api.current.remove(widgetId.current);
      widgetId.current = null;
    };
  }, [siteKey, visible]);

  useImperativeHandle(
    ref,
    () => ({
      reset: () => {
        onTokenRef.current(null);
        if (api.current && widgetId.current) api.current.reset(widgetId.current);
      },
    }),
    []
  );

  return <div ref={container} className={visible ? "plg-turnstile plg-turnstile--visible" : "plg-turnstile"} data-testid="turnstile" />;
});

export default TurnstileWidget;
